import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UtensilsCrossed, MapPin, Loader2, ArrowLeft, X, ImageOff, Plus, Minus, ShoppingBag, Trash2, Search, Star, Clock, Info, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { mediaUrl } from "../lib/mediaUrl";
import { isOpenNow } from "../lib/openingHours";
import SEOHead from "../components/SEOHead";

// Frais de livraison de base affichés à titre indicatif sur la vitrine.
// Le montant réel est calculé au checkout par le backend (distance, etc.).
const DELIVERY_FEE_EUR = 2.5;

// Vitrine publique restaurant Nold Go — layout Uber Eats :
// Desktop : entête 2 colonnes (infos resto à gauche + hero photo à droite),
//           puis sidebar catégories sticky à gauche + grille plats 2 colonnes à droite.
// Mobile  : hero pleine largeur puis infos, chips catégories horizontales sticky.
export default function PublicRestaurant() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [resto, setResto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [cart, setCart] = useState(() => loadCart(id));
  const [cartOpen, setCartOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);
  const [dishOpen, setDishOpen] = useState(null);
  const [activeTags, setActiveTags] = useState([]);
  const [scheduleMode, setScheduleMode] = useState("now"); // "now" | "later" — pré-sélection avant checkout
  const [schemaLd, setSchemaLd] = useState(null);
  const scrollingProgrammatically = useRef(false);

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const r = await fetch(`${API}/restaurants/${id}`);
        if (!r.ok) throw new Error("not_found");
        const data = await r.json();
        if (ok) setResto(data);
      } catch { if (ok) setErr("not_found"); }
      finally { if (ok) setLoading(false); }
    })();
    return () => { ok = false; };
  }, [id]);

  // Récupère le bloc JSON-LD Schema.org côté serveur (SEO restaurant).
  useEffect(() => {
    if (!resto?.id) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/restaurants/${resto.id}/schema.json`);
        if (r.ok) {
          const schema = await r.json();
          if (alive) setSchemaLd(schema);
        }
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [resto?.id]);

  const menuByCategory = useMemo(() => {
    const groups = {};
    (resto?.menu || []).filter((m) => m.active !== false).forEach((m) => {
      const cat = (m.category && m.category.trim()) || t("publicResto.otherCategory");
      groups[cat] = groups[cat] || [];
      groups[cat].push(m);
    });
    return Object.entries(groups);
  }, [resto, t]);

  // JSON-LD Menu (Schema.org) — enrichit les résultats Google avec les plats et prix.
  // Généré en local (sans appel backend) à partir du menu déjà chargé.
  const menuJsonLd = useMemo(() => {
    if (!resto?.id || menuByCategory.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Menu",
      name: `Carte de ${resto.name}`,
      inLanguage: "fr-BE",
      hasMenuSection: menuByCategory.map(([cat, items]) => ({
        "@type": "MenuSection",
        name: cat,
        hasMenuItem: items.slice(0, 40).map((it) => ({
          "@type": "MenuItem",
          name: it.name,
          ...(it.description ? { description: String(it.description).slice(0, 200) } : {}),
          ...(it.photo_url ? { image: String(it.photo_url).startsWith("http") ? it.photo_url : (window.location.origin + it.photo_url) } : {}),
          offers: {
            "@type": "Offer",
            price: Number(it.price_eur || 0).toFixed(2),
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
          },
        })),
      })),
    };
  }, [resto, menuByCategory]);

  // Combine le schéma Restaurant (backend) + le schéma Menu local en un @graph unique.
  const combinedJsonLd = useMemo(() => {
    if (!schemaLd && !menuJsonLd) return null;
    if (schemaLd && menuJsonLd) {
      return { "@context": "https://schema.org", "@graph": [schemaLd, menuJsonLd] };
    }
    return schemaLd || menuJsonLd;
  }, [schemaLd, menuJsonLd]);

  // Détecte des tags diététiques automatiquement à partir du nom + description
  // + tags explicites côté DB (item.tags). Fallback zéro backend requis.
  const dietTagsOf = (item) => {
    const explicit = Array.isArray(item.tags) ? item.tags.map((s) => String(s).toLowerCase()) : [];
    const txt = `${item.name || ""} ${item.description || ""}`.toLowerCase();
    const t2 = new Set(explicit);
    if (/(vegan|végan|100% végétal)/i.test(txt)) { t2.add("vegan"); t2.add("vegetarian"); }
    if (/(végét|vegetari|veggie|veggy|sans viande|végét)/i.test(txt)) t2.add("vegetarian");
    if (/(sans gluten|gluten-free|gluten free|glutenfree)/i.test(txt)) t2.add("gluten_free");
    if (/(épicé|epice|piquant|spicy|hot|pimenté|piment|harissa|sriracha|jalapeño|jalapeno)/i.test(txt)) t2.add("spicy");
    return t2;
  };

  const filteredMenuByCategory = useMemo(() => {
    const q = query.trim().toLowerCase();
    return menuByCategory
      .map(([cat, items]) => [cat, items.filter((it) => {
        if (q) {
          const okQuery = (it.name || "").toLowerCase().includes(q) || (it.description || "").toLowerCase().includes(q);
          if (!okQuery) return false;
        }
        if (activeTags.length > 0) {
          const tags = dietTagsOf(it);
          for (const tg of activeTags) {
            if (!tags.has(tg)) return false;
          }
        }
        return true;
      })])
      .filter(([, items]) => items.length > 0);
  }, [menuByCategory, query, activeTags]);

  const suggestions = useMemo(() => {
    const all = (resto?.menu || []).filter((m) => m.active !== false);
    const withPhoto = all.filter((m) => m.photo_url);
    const picks = withPhoto.length >= 4 ? withPhoto : all;
    return picks.slice(0, 6);
  }, [resto]);

  useEffect(() => {
    if (activeCat) return;
    if (suggestions.length > 0) { setActiveCat("__suggestions"); return; }
    if (filteredMenuByCategory.length > 0) setActiveCat(filteredMenuByCategory[0][0]);
  }, [suggestions, filteredMenuByCategory, activeCat]);

  useEffect(() => {
    const onScroll = () => {
      if (scrollingProgrammatically.current) return;
      const anchors = document.querySelectorAll("[data-cat-anchor]");
      let current = "";
      for (const el of anchors) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 180) current = el.getAttribute("data-cat-anchor") || "";
      }
      if (current && current !== activeCat) setActiveCat(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [activeCat]);

  const scrollToCat = (catKey) => {
    const el = document.querySelector(`[data-cat-anchor="${catKey}"]`);
    if (!el) return;
    scrollingProgrammatically.current = true;
    const top = el.getBoundingClientRect().top + window.scrollY - 150;
    window.scrollTo({ top, behavior: "smooth" });
    setActiveCat(catKey);
    setTimeout(() => { scrollingProgrammatically.current = false; }, 700);
  };

  const addToCart = (item, opts = null) => {
    // opts : { groups: [{group_id, group_name, required, choices:[{id,name,extra}]}], extras_eur }
    // Encodage : id = itemId::hash(opts) pour ne pas fusionner des variantes différentes.
    // Le name affiché côté restaurateur inclut les options (whitespace-pre-line).
    const extras = Number(opts?.extras_eur || 0);
    const optionsLabel = opts?.groups?.length ? (
      opts.groups.map((g) => {
        const choices = (g.choices || []).map((c) => c.extra > 0 ? `${c.name} (+${c.extra.toFixed(2).replace(".", ",")} €)` : c.name).join(", ");
        return choices ? `↳ ${g.group_name} : ${choices}` : "";
      }).filter(Boolean).join("\n")
    ) : "";
    const displayName = optionsLabel ? `${item.name}\n${optionsLabel}` : item.name;
    // Hash déterministe des choix pour ID de ligne panier unique par combinaison.
    const optsKey = opts?.groups?.length
      ? opts.groups.map((g) => `${g.group_id}:${(g.choices || []).map((c) => c.id).sort().join(",")}`).join("|")
      : "";
    const lineId = optsKey ? `${item.id}::${optsKey}` : item.id;
    const unitPrice = Number(item.price_eur) + extras;
    setCart((c) => {
      const idx = c.items.findIndex((x) => x.id === lineId);
      let next;
      if (idx >= 0) next = { ...c, items: c.items.map((x, i) => i === idx ? { ...x, qty: x.qty + 1 } : x) };
      else next = { ...c, restaurant_id: resto.id, restaurant_name: resto.name, items: [...c.items, { id: lineId, base_id: item.id, name: displayName, price_eur: unitPrice, qty: 1 }] };
      saveCart(resto.id, next);
      return next;
    });
    toast.success(t("publicResto.addedToCart", { name: item.name }));
  };
  const decCart = (itemId) => {
    setCart((c) => {
      const next = { ...c, items: c.items.map((x) => x.id === itemId ? { ...x, qty: x.qty - 1 } : x).filter((x) => x.qty > 0) };
      saveCart(resto?.id, next);
      return next;
    });
  };
  const removeFromCart = (itemId) => {
    setCart((c) => {
      const next = { ...c, items: c.items.filter((x) => x.id !== itemId) };
      saveCart(resto?.id, next);
      return next;
    });
  };
  const clearCart = () => {
    const next = emptyCart();
    saveCart(resto?.id, next);
    setCart(next);
  };
  // qtyOf compte le total des lignes correspondant à un plat de base (toutes variantes options confondues).
  const qtyOf = (itemId) => cart.items.reduce((s, x) => (x.base_id === itemId || x.id === itemId) ? s + x.qty : s, 0);

  const cartCount = cart.items.reduce((s, x) => s + x.qty, 0);
  const cartTotal = cart.items.reduce((s, x) => s + x.qty * x.price_eur, 0);

  const goOrder = () => {
    if (cartCount === 0) { toast.info("Ajoutez des plats au panier pour commander."); return; }
    const minOrder = Number(resto?.min_order_eur || 0);
    if (minOrder > 0 && cartTotal < minOrder) {
      toast.error(`Minimum de commande : ${fmtEuro(minOrder)} — il manque ${fmtEuro(minOrder - cartTotal)}`);
      return;
    }
    // On stocke la préférence "Immédiat / Plus tard" — le checkout la lit pour pré-sélectionner le mode.
    try {
      localStorage.setItem("nold_active_cart", JSON.stringify({
        ...cart,
        restaurant_id: resto.id,
        restaurant_name: resto.name,
        schedule_pref: scheduleMode,
      }));
    } catch { /* ignore */ }
    navigate(`/food/checkout`);
  };
  const toggleLang = () => i18n.changeLanguage(i18n.language === "fr" ? "en" : "fr");

  // Statut « Ouvert / Fermé » recalculé chaque minute à partir de opening_hours_text.
  // ⚠ Hooks placés AVANT les early-returns pour respecter les Rules of Hooks (ordre stable).
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(iv);
  }, []);
  const openStatus = useMemo(
    () => isOpenNow(resto?.opening_hours_text, new Date(nowTick)),
    [resto?.opening_hours_text, nowTick]
  );

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-8 h-8 animate-spin text-[#059669]" /></div>;
  if (err || !resto) return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <ImageOff className="w-12 h-12 text-[#75807A] mx-auto mb-3" />
        <h1 className="font-head font-black text-2xl">{t("publicResto.notFound.title")}</h1>
        <p className="text-sm text-[#75807A] mt-2">{t("publicResto.notFound.desc")}</p>
        <button onClick={() => navigate("/")} data-testid="pr-home" className="mt-4 h-11 px-6 rounded-full bg-[#059669] text-white font-bold">{t("publicResto.backHome")}</button>
      </div>
    </div>
  );

  const gallery = resto.gallery || [];
  const heroPhoto = resto.cover_url || gallery[0]?.url;
  const rating = resto.rating || resto.avg_rating || 4.7;
  const reviews = resto.review_count || null;
  const hours = resto.hours_summary || resto.opening_hours_summary || null;

  const navItems = [];
  if (suggestions.length > 0) navItems.push({ key: "__suggestions", label: t("publicResto.suggestionsTitle", "Suggestions") });
  filteredMenuByCategory.forEach(([cat]) => navItems.push({ key: cat, label: cat }));

  return (
    <div className="min-h-screen bg-white pb-32 xl:pb-24" data-testid="public-restaurant">
      <SEOHead
        title={`${resto.name} · Nold Go`}
        description={(resto.description || `Découvrez la carte de ${resto.name} sur Nold Go — Livraison à vélo à Bruxelles.`).slice(0, 160)}
        canonical={`/restaurants/${resto.id}`}
        type="restaurant.restaurant"
        image={(resto.gallery && resto.gallery[0]?.url) ? resto.gallery[0].url : undefined}
        jsonLd={combinedJsonLd || undefined}
      />
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E2DFD8]">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <button onClick={() => navigate(-1)} data-testid="pr-back" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full hover:bg-[#F0EBE1] font-semibold text-sm text-[#1A1A1A]">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">{t("common.back")}</span>
          </button>
          <img src="/nold-logo.png" alt="Nold Go Food" className="h-10 sm:h-12 object-contain" />
          <button onClick={toggleLang} data-testid="pr-lang" className="h-9 px-3 rounded-full hover:bg-[#F0EBE1] font-bold text-xs text-[#1A1A1A]">
            {i18n.language === "fr" ? "🇬🇧 EN" : "🇫🇷 FR"}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        {/* Entête restaurant : 2 colonnes desktop (infos gauche + hero droite), 1 col mobile */}
        <div className="pt-4 sm:pt-6 grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-4 lg:gap-8 items-start">
          {/* Infos resto */}
          <div className="order-2 lg:order-1" data-testid="pr-resto-header">
            <h1 className="font-head font-black text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A] leading-tight">{resto.name}</h1>
            {openStatus !== null && (
              <div className="mt-2">
                {openStatus ? (
                  <span
                    data-testid="pr-open-badge-open"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#DCFCE7] border border-[#16A34A] text-[#166534] px-3 py-1 text-xs font-bold"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" /> Ouvert maintenant
                  </span>
                ) : (
                  <span
                    data-testid="pr-open-badge-closed"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#FEE2E2] border border-[#DC2626] text-[#991B1B] px-3 py-1 text-xs font-bold"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#DC2626]" /> Fermé
                  </span>
                )}
              </div>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="pr-fees-info">
              <span className="inline-flex items-center gap-1 bg-[#F5F1E8] rounded-full px-2.5 py-1 font-semibold text-[#3B423E]">
                🛵 Livraison {fmtEuro(DELIVERY_FEE_EUR)}
              </span>
              {Number(resto?.min_order_eur) > 0 && (
                <span className="inline-flex items-center gap-1 bg-[#F5F1E8] rounded-full px-2.5 py-1 font-semibold text-[#3B423E]" data-testid="pr-min-order-badge">
                  💰 Min. {fmtEuro(resto.min_order_eur)}
                </span>
              )}
              {Number(resto?.delivery_time_min) > 0 && (
                <span className="inline-flex items-center gap-1 bg-[#F5F1E8] rounded-full px-2.5 py-1 font-semibold text-[#3B423E]" data-testid="pr-delivery-time-badge">
                  🕐 {resto.delivery_time_min} min
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-[#3B423E]">
              <span className="inline-flex items-center gap-1 font-bold text-[#1A1A1A]">
                <Star className="w-4 h-4 fill-[#F26419] text-[#F26419]" /> {Number(rating).toFixed(1)}
              </span>
              {reviews && <span className="text-[#75807A]">({reviews}+)</span>}
              {resto.cuisine && (
                <>
                  <span className="text-[#75807A]">·</span>
                  <span className="text-[#3B423E]">{resto.cuisine}</span>
                </>
              )}
              {resto.delivery_time_min && (
                <>
                  <span className="text-[#75807A]">·</span>
                  <span className="inline-flex items-center gap-1 text-[#3B423E]"><Clock className="w-3.5 h-3.5" /> {resto.delivery_time_min} min</span>
                </>
              )}
            </div>
            {(resto.address || resto.city) && (
              <div className="mt-1.5 flex items-center gap-1 text-sm text-[#75807A]">
                <MapPin className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{[resto.address, resto.city].filter(Boolean).join(", ")}</span>
              </div>
            )}
            {hours && <div className="mt-1 text-xs text-[#75807A]">{hours}</div>}
            <button
              onClick={() => setInfoOpen(true)}
              data-testid="pr-store-info"
              className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#F5F1E8] hover:bg-[#E2DFD8] text-[#1A1A1A] font-semibold text-sm"
            >
              <Info className="w-4 h-4" /> {t("publicResto.storeInfo", "Infos restaurant")}
            </button>
          </div>

          {/* Hero photo */}
          <div className="order-1 lg:order-2 relative w-full aspect-[16/8] sm:aspect-[16/7] lg:aspect-[16/6] rounded-2xl overflow-hidden bg-[#F0EBE1]" data-testid="pr-hero">
            {heroPhoto ? (
              <img src={heroPhoto} alt={resto.name} className="w-full h-full object-cover" loading="eager" />
            ) : (
              <div className="w-full h-full grid place-items-center bg-gradient-to-br from-[#059669] to-[#047857]">
                <UtensilsCrossed className="w-20 h-20 text-white/30" />
              </div>
            )}
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="mt-5 sm:mt-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#75807A]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("publicResto.searchDishIn", { name: resto.name, defaultValue: `Rechercher dans ${resto.name}` })}
              data-testid="pr-search"
              className="w-full h-12 pl-11 pr-11 rounded-full bg-white border border-[#E2DFD8] focus:border-[#1A1A1A] outline-none text-sm placeholder:text-[#75807A]"
            />
            {query && (
              <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-[#F0EBE1] grid place-items-center" data-testid="pr-search-clear">
                <X className="w-4 h-4 text-[#75807A]" />
              </button>
            )}
          </div>
          {/* Filtres diététiques rapides */}
          <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="pr-diet-filters">
            {[
              { key: "vegetarian", label: "🌱 Végé" },
              { key: "vegan", label: "🥬 Végan" },
              { key: "gluten_free", label: "🌾 Sans gluten" },
              { key: "spicy", label: "🌶️ Épicé" },
            ].map((f) => {
              const active = activeTags.includes(f.key);
              return (
                <button
                  key={f.key}
                  onClick={() => setActiveTags((prev) => active ? prev.filter((x) => x !== f.key) : [...prev, f.key])}
                  data-testid={`pr-diet-${f.key}`}
                  className={`shrink-0 h-8 px-3 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border ${
                    active ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white text-[#1A1A1A] border-[#E2DFD8] hover:border-[#1A1A1A]"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
            {activeTags.length > 0 && (
              <button
                onClick={() => setActiveTags([])}
                data-testid="pr-diet-clear"
                className="shrink-0 h-8 px-3 rounded-full text-xs font-semibold text-[#75807A] hover:text-[#1A1A1A] whitespace-nowrap"
              >
                × Effacer
              </button>
            )}
          </div>
        </div>

        {/* Layout 3 colonnes : sidebar catégories + grille plats + panier sticky (desktop uniquement) */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-[200px_1fr] xl:grid-cols-[200px_1fr_320px] gap-6 lg:gap-8 pt-2">
          {/* Sidebar / chips */}
          <aside data-testid="pr-cat-nav">
            {/* Desktop */}
            <div className="hidden lg:block lg:sticky lg:top-20 self-start">
              <div className="pb-3 mb-2 border-b border-[#E2DFD8]">
                <div className="font-head font-black text-lg text-[#1A1A1A]">Menu</div>
                {hours && <div className="text-xs text-[#75807A] mt-0.5">{hours}</div>}
              </div>
              <ul className="space-y-0.5 max-h-[70vh] overflow-y-auto no-scrollbar">
                {navItems.map((n) => (
                  <li key={n.key}>
                    <button
                      onClick={() => scrollToCat(n.key)}
                      data-testid={`pr-cat-btn-${slug(n.key)}`}
                      className={`w-full text-left pl-3 pr-2 py-2 rounded-lg text-[15px] font-semibold transition-colors relative ${
                        activeCat === n.key
                          ? "bg-[#F5F1E8] text-[#1A1A1A]"
                          : "text-[#1A1A1A] hover:bg-[#F5F1E8]/60"
                      }`}
                    >
                      {activeCat === n.key && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r bg-[#1A1A1A]" />
                      )}
                      {n.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            {/* Mobile chips */}
            <div className="lg:hidden sticky top-16 z-30 -mx-4 px-4 bg-white/95 backdrop-blur border-b border-[#E2DFD8]">
              <div className="flex gap-2 overflow-x-auto py-2 no-scrollbar" data-testid="pr-cat-chips">
                {navItems.map((n) => (
                  <button
                    key={n.key}
                    onClick={() => scrollToCat(n.key)}
                    data-testid={`pr-cat-chip-${slug(n.key)}`}
                    className={`shrink-0 h-9 px-4 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${
                      activeCat === n.key
                        ? "bg-[#1A1A1A] text-white"
                        : "bg-[#F5F1E8] text-[#1A1A1A] hover:bg-[#E2DFD8]"
                    }`}
                  >
                    {n.label}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Grille plats */}
          <main className="min-w-0 space-y-10">
            {suggestions.length > 0 && (
              <section data-cat-anchor="__suggestions" data-testid="pr-section-suggestions">
                <h2 className="font-head font-black text-2xl sm:text-3xl text-[#1A1A1A]">
                  {t("publicResto.suggestionsTitle", "Suggestions")}
                </h2>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                  {suggestions.map((m) => (
                    <DishCard key={`sug-${m.id}`} item={m} qty={qtyOf(m.id)} onAdd={() => (m.option_groups?.length ? setDishOpen(m) : addToCart(m))} onDec={() => decCart(m.id)} onOpen={() => setDishOpen(m)} />
                  ))}
                </ul>
              </section>
            )}

            {filteredMenuByCategory.length === 0 ? (
              <div className="rounded-2xl bg-[#FAF5EC] border border-[#E2DFD8] p-8 text-center">
                <UtensilsCrossed className="w-8 h-8 text-[#75807A] mx-auto mb-2" />
                <p className="text-sm text-[#75807A]">
                  {query ? t("publicResto.searchNoResult", "Aucun plat ne correspond à votre recherche.") : t("publicResto.menuEmpty")}
                </p>
              </div>
            ) : (
              filteredMenuByCategory.map(([category, items]) => (
                <section key={category} data-cat-anchor={category} data-testid={`pr-section-${slug(category)}`}>
                  <h2 className="font-head font-black text-2xl sm:text-3xl text-[#1A1A1A]">{category}</h2>
                  <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    {items.map((m) => (
                      <DishCard key={m.id} item={m} qty={qtyOf(m.id)} onAdd={() => (m.option_groups?.length ? setDishOpen(m) : addToCart(m))} onDec={() => decCart(m.id)} onOpen={() => setDishOpen(m)} />
                    ))}
                  </ul>
                </section>
              ))
            )}

            {gallery.length > 0 && (
              <section className="pt-6 border-t border-[#E2DFD8]">
                <h2 className="font-head font-black text-xl text-[#1A1A1A]">{t("publicResto.galleryTitle")}</h2>
                <ul className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {gallery.map((g) => (
                    <li key={g.id} className="relative rounded-xl overflow-hidden aspect-square bg-[#F0EBE1] cursor-pointer group" onClick={() => setLightbox(g)} data-testid={`pr-gallery-${g.id}`}>
                      <img src={g.url} alt={g.caption || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </main>

          {/* Panier sticky desktop (xl+) — colonne fixe à droite qui suit le scroll */}
          <aside className="hidden xl:block">
            <div className="sticky top-20">
              <CartColumn
                cart={cart}
                onDec={decCart}
                onAdd={addToCart}
                onRemove={removeFromCart}
                onOrder={goOrder}
                scheduleMode={scheduleMode}
                onScheduleModeChange={setScheduleMode}
                minOrder={Number(resto?.min_order_eur || 0)}
              />
            </div>
          </aside>
        </div>

        <DiscoverSection currentId={resto.id} />

        <FaqSection resto={resto} />

        <footer className="text-center text-xs text-[#75807A] pt-8 pb-4 border-t border-[#E2DFD8] mt-12">
          Nold Go SRL · Rue Jourdan 71 A0 · 1060 Saint-Gilles · Bruxelles · Belgique · TVA BE 1040.580.267
        </footer>
      </div>

      {/* Bandeau panier détaillé (mobile + tablette). Sur xl+, la colonne sticky remplace ce bandeau. */}
      {cartCount > 0 && (
        <div className="xl:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E2DFD8] shadow-[0_-8px_20px_rgba(0,0,0,0.06)]" data-testid="pr-cart-bottom">
          {/* Toggle Immédiat / Plus tard (mobile) */}
          <div className="max-w-7xl mx-auto px-3 pt-2">
            <div className="rounded-lg bg-[#F5F1E8] p-1 grid grid-cols-2 gap-1" data-testid="pr-cart-mobile-schedule">
              <button
                type="button"
                onClick={() => setScheduleMode("now")}
                data-testid="pr-cart-mobile-schedule-now"
                className={`h-8 rounded-md font-semibold text-[11px] transition-colors ${scheduleMode === "now" ? "bg-[#059669] text-white" : "text-[#3B423E]"}`}
              >
                🛵 Immédiat
              </button>
              <button
                type="button"
                onClick={() => setScheduleMode("later")}
                title="Choisir date et heure au checkout"
                data-testid="pr-cart-mobile-schedule-later"
                className={`h-8 rounded-md font-semibold text-[11px] transition-colors ${scheduleMode === "later" ? "bg-[#059669] text-white" : "text-[#3B423E]"}`}
              >
                📅 Plus tard
              </button>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-3 py-2.5 flex items-center gap-3">
            <button
              onClick={() => setCartOpen(true)}
              className="flex-1 min-w-0 text-left"
              data-testid="pr-cart-fab"
              aria-label="Voir le détail du panier"
            >
              <div className="flex items-center gap-2 text-[11px] font-bold text-[#75807A] uppercase tracking-wide">
                <ShoppingBag className="w-3.5 h-3.5" /> Panier · {cartCount}
              </div>
              <div className="mt-0.5 flex gap-1.5 overflow-x-auto no-scrollbar text-sm text-[#1A1A1A]">
                {cart.items.map((it) => (
                  <span key={it.id} className="shrink-0 inline-flex items-center gap-1 bg-[#F5F1E8] rounded-full px-2.5 py-0.5 text-xs">
                    <b className="text-[#F26419]">{it.qty}×</b> <span className="truncate max-w-[100px]">{it.name}</span>
                  </span>
                ))}
              </div>
            </button>
            <div className="shrink-0 text-right">
              <div className="text-[11px] text-[#75807A]">Total</div>
              <div className="font-head font-black text-base text-[#1A1A1A] leading-none">{fmtEuro(cartTotal)}</div>
            </div>
            <button
              onClick={goOrder}
              disabled={Number(resto?.min_order_eur || 0) > 0 && cartTotal < Number(resto?.min_order_eur || 0)}
              data-testid="pr-cart-order-quick"
              className="shrink-0 h-11 px-5 rounded-full bg-[#1A1A1A] hover:bg-black text-white font-bold text-sm inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              title={Number(resto?.min_order_eur || 0) > 0 && cartTotal < Number(resto?.min_order_eur || 0) ? `Minimum ${fmtEuro(resto.min_order_eur)} — il manque ${fmtEuro(resto.min_order_eur - cartTotal)}` : ""}
            >
              🛵 {Number(resto?.min_order_eur || 0) > 0 && cartTotal < Number(resto?.min_order_eur || 0) ? `Min. ${fmtEuro(resto.min_order_eur)}` : "Commander"}
            </button>
          </div>
        </div>
      )}

      {cartOpen && (
        <CartDrawer cart={cart} onClose={() => setCartOpen(false)} onAdd={(id) => addToCart({ id, ...cart.items.find((x) => x.id === id) })}
          onDec={decCart} onRemove={removeFromCart} onClear={clearCart} onOrder={goOrder} />
      )}

      {infoOpen && (
        <InfoModal resto={resto} onClose={() => setInfoOpen(false)} />
      )}

      {dishOpen && (
        <DishModal
          item={dishOpen}
          menuItems={resto?.menu || []}
          qtyOf={qtyOf}
          onAddItem={(it) => addToCart(it)}
          onClose={() => setDishOpen(null)}
          onAddToCart={(it, opts) => addToCart(it, opts)}
        />
      )}

      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="pr-lightbox">
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/95 grid place-items-center"><X className="w-5 h-5" /></button>
          <img src={lightbox.url} alt={lightbox.caption || ""} className="max-w-full max-h-full object-contain rounded-xl" />
        </div>
      )}
    </div>
  );
}

// Carte plat style Uber Eats
// Carte plat style Uber Eats — clic n'importe où sur la carte ouvre la modale plat.
function DishCard({ item, qty, onAdd, onDec, onOpen }) {
  const { t } = useTranslation();
  const handleAdd = (e) => { e.stopPropagation(); onAdd(); };
  const handleDec = (e) => { e.stopPropagation(); onDec(); };
  return (
    <li
      className="group flex gap-3 sm:gap-4 p-2 sm:p-3 rounded-2xl hover:bg-[#F9F6EF] transition-colors border border-transparent hover:border-[#E2DFD8] cursor-pointer"
      onClick={onOpen}
      data-testid={`pr-dish-${item.id}`}
    >
      <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
        <div>
          <div className="font-semibold text-[#1A1A1A] text-[15px] sm:text-base leading-snug line-clamp-2">{item.name}</div>
          <div className="mt-1 font-semibold text-[15px] text-[#1A1A1A]">{fmtEuro(item.price_eur)}</div>
          {item.description && (
            <p className="mt-1.5 text-xs sm:text-sm text-[#75807A] line-clamp-2 leading-relaxed">{item.description}</p>
          )}
        </div>
      </div>
      <div className="relative w-28 h-28 sm:w-32 sm:h-32 shrink-0 self-start">
        {item.photo_url ? (
          <img src={item.photo_url} alt={item.name} className="w-full h-full object-cover rounded-2xl" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-[#F0EBE1] rounded-2xl grid place-items-center">
            <UtensilsCrossed className="w-8 h-8 text-[#75807A]" />
          </div>
        )}
        {qty === 0 ? (
          <button
            onClick={handleAdd}
            data-testid={`pr-add-${item.id}`}
            aria-label={t("publicResto.addToCart")}
            className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-white border border-[#E2DFD8] shadow-md hover:shadow-lg hover:border-[#1A1A1A] grid place-items-center transition-all"
          >
            <Plus className="w-5 h-5 text-[#1A1A1A]" strokeWidth={2.75} />
          </button>
        ) : (
          <div onClick={(e) => e.stopPropagation()} className="absolute -bottom-2 -right-2 inline-flex items-center bg-white rounded-full shadow-md border border-[#E2DFD8] p-0.5" data-testid={`pr-qty-${item.id}`}>
            <button onClick={handleDec} className="w-8 h-8 rounded-full grid place-items-center hover:bg-[#F0EBE1]" aria-label="Retirer">
              <Minus className="w-4 h-4 text-[#1A1A1A]" strokeWidth={2.5} />
            </button>
            <span className="min-w-5 text-center font-bold text-sm text-[#1A1A1A] px-1">{qty}</span>
            <button onClick={handleAdd} className="w-8 h-8 rounded-full bg-[#1A1A1A] hover:bg-black text-white grid place-items-center" aria-label="Ajouter">
              <Plus className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

// Modale plein écran style Uber Eats — layout 2 colonnes desktop (photo | détails)
// + section « Fréquemment achetés ensemble » (priorité boissons)
function DishModal({ item, menuItems = [], qtyOf, onAddItem, onClose, onAddToCart }) {
  // Layout Deliveroo : grande photo pleine largeur, nom+prix, description, groupes d'options, bouton fixé en bas.
  const groups = Array.isArray(item.option_groups) ? item.option_groups : [];
  const [selections, setSelections] = useState(() => {
    // Pré-sélectionne les choix par défaut définis par le restaurateur (default_choice_id sur chaque groupe).
    const init = {};
    for (const g of groups) {
      if (g.default_choice_id && (g.choices || []).some((c) => c.id === g.default_choice_id)) {
        init[g.id] = [g.default_choice_id];
      }
    }
    return init;
  }); // { [group_id]: [choice_id, ...] }
  const [dishQty, setDishQty] = useState(1);

  // Sélection "Fréquemment achetés ensemble" — logique contextuelle selon type de l'article ouvert.
  //  · Item = boisson  → suggère des plats principaux
  //  · Item = plat/dessert/autre → priorité boissons, sinon desserts
  const suggestions = useMemo(() => {
    const norm = (c) => String(c || "").toLowerCase();
    const isDrink = (x) => { const c = norm(x.category); return c.includes("boisson") || c.includes("drink"); };
    const isDessert = (x) => { const c = norm(x.category); return c.includes("dessert") || c.includes("sucré") || c.includes("pâtisserie"); };
    const isMain = (x) => {
      const c = norm(x.category);
      return ["plat", "pizza", "burger", "entrée", "entree", "wrap", "salade", "sushi", "pasta", "pâte", "sandwich", "tacos", "kebab", "menu"].some((k) => c.includes(k));
    };
    const currentCat = norm(item.category);
    const others = menuItems.filter((x) => x.id !== item.id && norm(x.category) !== currentCat);
    const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
    const itemIsDrink = isDrink(item);
    let pool = [];
    if (itemIsDrink) {
      pool = shuffle(others.filter(isMain));
      if (pool.length < 3) pool = [...pool, ...shuffle(others.filter((x) => !pool.includes(x) && !isDrink(x)))];
    } else {
      const drinks = shuffle(others.filter(isDrink));
      const desserts = shuffle(others.filter(isDessert));
      pool = drinks.length > 0 ? drinks : desserts;
      if (pool.length < 3) pool = [...pool, ...shuffle(others.filter((x) => !pool.includes(x) && !isDrink(x) && !isDessert(x)))];
    }
    return pool.slice(0, 3);
  }, [item.id, item.category, menuItems]);

  const toggleChoice = (group, choiceId) => {
    setSelections((s) => {
      const cur = s[group.id] || [];
      const isRadio = group.required && group.max_choices === 1;
      if (isRadio) return { ...s, [group.id]: [choiceId] };
      // Checkbox : toggle avec respect du max
      if (cur.includes(choiceId)) return { ...s, [group.id]: cur.filter((id) => id !== choiceId) };
      const max = Math.max(1, group.max_choices || 1);
      if (cur.length >= max) return s;
      return { ...s, [group.id]: [...cur, choiceId] };
    });
  };

  // Somme des suppléments (€) selon les choix courants.
  const extrasEur = useMemo(() => {
    let sum = 0;
    for (const g of groups) {
      const sel = selections[g.id] || [];
      for (const cid of sel) {
        const c = (g.choices || []).find((x) => x.id === cid);
        if (c) sum += Number(c.extra_price_eur || 0);
      }
    }
    return sum;
  }, [groups, selections]);

  // Vérifie que chaque groupe obligatoire a bien son min de choix.
  const canAdd = groups.every((g) => {
    if (!g.required) return true;
    const sel = selections[g.id] || [];
    const need = Math.max(1, g.min_choices || 1);
    return sel.length >= need;
  });

  const totalPrice = (Number(item.price_eur) + extrasEur) * dishQty;

  const handleAdd = () => {
    if (!canAdd) return;
    const chosenGroups = groups.map((g) => {
      const sel = selections[g.id] || [];
      const chosen = sel.map((cid) => {
        const c = (g.choices || []).find((x) => x.id === cid);
        return c ? { id: c.id, name: c.name, extra: Number(c.extra_price_eur || 0) } : null;
      }).filter(Boolean);
      return chosen.length > 0 ? { group_id: g.id, group_name: g.name, required: !!g.required, choices: chosen } : null;
    }).filter(Boolean);
    const opts = chosenGroups.length > 0 ? { groups: chosenGroups, extras_eur: extrasEur } : null;
    // Ajoute dishQty fois : chaque appel incrémente la même ligne (même hash options).
    for (let i = 0; i < dishQty; i++) onAddToCart(item, opts);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="pr-dish-modal">
      <div
        className="bg-white w-full sm:w-[92vw] sm:max-w-[560px] sm:rounded-3xl rounded-t-3xl max-h-[100vh] sm:max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Zone scrollable : photo + infos + options + suggestions */}
        <div className="flex-1 overflow-y-auto">
          {/* Grande photo pleine largeur */}
          <div className="relative">
            {item.photo_url ? (
              <img src={item.photo_url} alt={item.name} className="w-full aspect-[16/10] object-cover" />
            ) : (
              <div className="w-full aspect-[16/10] bg-[#F0EBE1] grid place-items-center">
                <UtensilsCrossed className="w-12 h-12 text-[#75807A]" />
              </div>
            )}
            <button onClick={onClose} data-testid="pr-dish-modal-close"
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/95 hover:bg-white grid place-items-center shadow-md">
              <X className="w-5 h-5 text-[#1A1A1A]" />
            </button>
          </div>
          {/* Nom + prix + description */}
          <div className="p-5 sm:p-6">
            <h2 className="font-head font-black text-2xl sm:text-3xl text-[#1A1A1A] leading-tight" data-testid="pr-dish-modal-name">{item.name}</h2>
            <div className="mt-2 font-head font-bold text-xl text-[#1A1A1A]">{fmtEuro(item.price_eur)}</div>
            {item.description && (
              <p className="mt-4 text-sm sm:text-base text-[#3B423E] leading-relaxed whitespace-pre-line" data-testid="pr-dish-modal-desc">{item.description}</p>
            )}
          </div>

          {/* Groupes d'options */}
          {groups.length > 0 && (
            <div className="border-t border-[#E2DFD8]" data-testid="pr-dish-modal-options">
              {groups.map((g) => (
                <OptionGroupBlock key={g.id} group={g} selected={selections[g.id] || []} onToggle={(cid) => toggleChoice(g, cid)} />
              ))}
            </div>
          )}

          {/* Section « Fréquemment achetés ensemble » — déplacée sous les options */}
          {suggestions.length > 0 && (
            <div className="border-t border-[#E2DFD8] p-5 sm:p-6" data-testid="pr-dish-modal-suggestions">
              <h3 className="font-head font-black text-lg text-[#1A1A1A] mb-3">Fréquemment achetés ensemble</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {suggestions.map((s) => {
                  const sQty = qtyOf ? qtyOf(s.id) : 0;
                  return (
                    <div key={s.id} data-testid={`pr-dish-modal-suggestion-${s.id}`}
                      className="relative rounded-2xl border border-[#E2DFD8] bg-white overflow-hidden hover:border-[#1A1A1A] transition-colors">
                      <div className="relative w-full aspect-[4/3] bg-[#F0EBE1]">
                        {s.photo_url ? (
                          <img src={s.photo_url} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full grid place-items-center"><UtensilsCrossed className="w-6 h-6 text-[#75807A]" /></div>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); onAddItem && onAddItem(s); }}
                          data-testid={`pr-dish-modal-suggestion-add-${s.id}`}
                          aria-label={`Ajouter ${s.name}`}
                          className="absolute -bottom-2 -right-2 w-9 h-9 rounded-full bg-white border border-[#E2DFD8] shadow-md hover:shadow-lg hover:border-[#1A1A1A] grid place-items-center transition-all"
                        >
                          {sQty === 0 ? <Plus className="w-4 h-4 text-[#1A1A1A]" strokeWidth={2.75} /> : (
                            <span className="text-xs font-black text-[#1A1A1A]">{sQty}</span>
                          )}
                        </button>
                      </div>
                      <div className="p-2.5">
                        <div className="font-semibold text-[13px] text-[#1A1A1A] leading-snug line-clamp-2">{s.name}</div>
                        <div className="mt-0.5 font-semibold text-[13px] text-[#1A1A1A]">{fmtEuro(s.price_eur)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Barre bouton fixée en bas — quantité + Ajouter au panier + prix total temps réel */}
        <div className="p-4 sm:p-5 border-t border-[#E2DFD8] bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center bg-[#F5F1E8] rounded-full p-1">
              <button onClick={() => setDishQty((q) => Math.max(1, q - 1))} disabled={dishQty === 1}
                data-testid="pr-dish-modal-qty-dec"
                className="w-10 h-10 rounded-full bg-white grid place-items-center hover:bg-[#F0EBE1] disabled:opacity-40" aria-label="Retirer">
                <Minus className="w-5 h-5 text-[#1A1A1A]" strokeWidth={2.5} />
              </button>
              <span className="min-w-8 text-center font-bold text-lg text-[#1A1A1A] px-2" data-testid="pr-dish-modal-qty">{dishQty}</span>
              <button onClick={() => setDishQty((q) => q + 1)} data-testid="pr-dish-modal-qty-inc"
                className="w-10 h-10 rounded-full bg-[#1A1A1A] hover:bg-black text-white grid place-items-center" aria-label="Ajouter">
                <Plus className="w-5 h-5" strokeWidth={2.5} />
              </button>
            </div>
            <button
              onClick={handleAdd}
              disabled={!canAdd}
              data-testid="pr-dish-modal-add"
              className="flex-1 h-12 rounded-full bg-[#1A1A1A] hover:bg-black text-white font-bold text-base inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5" strokeWidth={2.5} /> Ajouter au panier · {fmtEuro(totalPrice)}
            </button>
          </div>
          {!canAdd && (
            <div className="mt-2 text-center text-xs text-[#D32F2F] font-bold" data-testid="pr-dish-modal-required-hint">
              Sélectionnez les options obligatoires pour continuer.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OptionGroupBlock({ group, selected, onToggle }) {
  const isRadio = !!group.required && (group.max_choices || 1) === 1;
  const max = Math.max(1, group.max_choices || 1);
  const min = Math.max(0, group.min_choices || 0);
  return (
    <div className="p-5 sm:p-6 border-t border-[#F0EBE1] first:border-t-0" data-testid={`pr-option-group-${group.id}`}>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <h3 className="font-head font-black text-[#1A1A1A]">{group.name}</h3>
        {group.required ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#DC2626] text-white text-[10px] font-black uppercase tracking-wide" data-testid={`pr-option-required-${group.id}`}>
            Obligatoire
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#F0EBE1] text-[#75807A] text-[10px] font-bold uppercase tracking-wide">
            Facultatif
          </span>
        )}
      </div>
      {max > 1 && (
        <div className="text-xs text-[#75807A] mb-3">
          {group.required
            ? `Choisissez ${min === max ? max : `${Math.max(min, 1)} à ${max}`} option${max > 1 ? "s" : ""}`
            : `Jusqu'à ${max} choix`}
        </div>
      )}
      <ul className="space-y-2 mt-2">
        {(group.choices || []).map((c) => {
          const isSelected = selected.includes(c.id);
          return (
            <li key={c.id}>
              <label
                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${isSelected ? "border-[#1A1A1A] bg-[#F9F6EF]" : "border-[#E2DFD8] hover:border-[#75807A]"}`}
                data-testid={`pr-option-choice-${c.id}`}
              >
                {isRadio ? (
                  <input
                    type="radio" name={`grp-${group.id}`} checked={isSelected}
                    onChange={() => onToggle(c.id)}
                    className="w-4 h-4 accent-[#1A1A1A]"
                    data-testid={`pr-option-choice-input-${c.id}`}
                  />
                ) : (
                  <input
                    type="checkbox" checked={isSelected}
                    onChange={() => onToggle(c.id)}
                    className="w-4 h-4 accent-[#1A1A1A]"
                    data-testid={`pr-option-choice-input-${c.id}`}
                  />
                )}
                <span className="flex-1 font-semibold text-sm text-[#1A1A1A]">{c.name}</span>
                {Number(c.extra_price_eur) > 0 && (
                  <span className="text-sm font-bold text-[#1A1A1A]">
                    +&nbsp;{Number(c.extra_price_eur).toFixed(2).replace(".", ",")}&nbsp;€
                  </span>
                )}
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Colonne panier sticky desktop — visible en permanence, affiche items + total + bouton Commander
function CartColumn({ cart, onDec, onAdd, onRemove, onOrder, scheduleMode = "now", onScheduleModeChange, minOrder = 0 }) {
  const total = cart.items.reduce((s, x) => s + x.qty * x.price_eur, 0);
  const count = cart.items.reduce((s, x) => s + x.qty, 0);
  const belowMin = minOrder > 0 && total < minOrder;
  const missing = belowMin ? (minOrder - total) : 0;
  if (count === 0) {
    return (
      <div className="rounded-2xl bg-[#F9F6EF] border border-[#E2DFD8] p-6 text-center" data-testid="pr-cart-column-empty">
        <ShoppingBag className="w-8 h-8 text-[#75807A] mx-auto mb-2" />
        <div className="font-semibold text-[#1A1A1A]">Votre panier est vide</div>
        <p className="mt-1 text-xs text-[#75807A]">Ajoutez des plats pour commander</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-white border border-[#E2DFD8] shadow-sm overflow-hidden" data-testid="pr-cart-column">
      <div className="p-4 border-b border-[#E2DFD8]">
        <div className="font-head font-black text-lg text-[#1A1A1A] inline-flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-[#F26419]" /> Votre commande
        </div>
        <div className="text-xs text-[#75807A] mt-0.5">{count} article{count > 1 ? "s" : ""}</div>
      </div>
      <ul className="max-h-[45vh] overflow-y-auto divide-y divide-[#F0EBE1]">
        {cart.items.map((it) => (
          <li key={it.id} className="p-3 flex items-start gap-2" data-testid={`pr-cart-col-item-${it.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-[#1A1A1A] line-clamp-1">{it.name}</div>
              <div className="text-xs text-[#75807A]">{fmtEuro(it.price_eur)} · sous-total <b className="text-[#1A1A1A]">{fmtEuro(it.price_eur * it.qty)}</b></div>
              <div className="mt-2 inline-flex items-center gap-0.5 bg-[#F5F1E8] rounded-full p-0.5">
                <button onClick={() => onDec(it.id)} data-testid={`pr-cart-col-dec-${it.id}`} className="w-7 h-7 rounded-full bg-white grid place-items-center hover:bg-[#F0EBE1]"><Minus className="w-3.5 h-3.5 text-[#1A1A1A]" /></button>
                <span className="min-w-6 text-center font-bold text-sm text-[#1A1A1A] px-1">{it.qty}</span>
                <button onClick={() => onAdd(it)} data-testid={`pr-cart-col-inc-${it.id}`} className="w-7 h-7 rounded-full bg-[#1A1A1A] text-white grid place-items-center hover:bg-black"><Plus className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <button onClick={() => onRemove(it.id)} data-testid={`pr-cart-col-remove-${it.id}`} className="w-7 h-7 rounded-full hover:bg-[#FEE2E2] grid place-items-center text-[#D32F2F] shrink-0" aria-label="Supprimer">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ul>
      <div className="p-4 border-t border-[#E2DFD8] bg-[#FDF8F0] space-y-3">
        {/* Toggle Immédiat / Plus tard — la sélection sera transmise au checkout */}
        <div className="rounded-xl bg-white border border-[#E2DFD8] p-1 grid grid-cols-2 gap-1" data-testid="pr-cart-schedule-toggle">
          <button
            type="button"
            onClick={() => onScheduleModeChange && onScheduleModeChange("now")}
            data-testid="pr-cart-schedule-now"
            className={`h-9 rounded-lg font-semibold text-xs transition-colors ${scheduleMode === "now" ? "bg-[#059669] text-white" : "text-[#3B423E] hover:bg-[#F5F1E8]"}`}
          >
            🛵 Immédiat
          </button>
          <button
            type="button"
            onClick={() => onScheduleModeChange && onScheduleModeChange("later")}
            title="Choisir date et heure au checkout"
            data-testid="pr-cart-schedule-later"
            className={`h-9 rounded-lg font-semibold text-xs transition-colors ${scheduleMode === "later" ? "bg-[#059669] text-white" : "text-[#3B423E] hover:bg-[#F5F1E8]"}`}
          >
            📅 Plus tard
          </button>
        </div>
        {scheduleMode === "later" && (
          <div className="text-[11px] text-[#059669] font-semibold text-center" data-testid="pr-cart-schedule-hint">
            Vous choisirez date et créneau au checkout.
          </div>
        )}
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-semibold text-[#3B423E]">Sous-total</span>
          <span className="font-head font-black text-xl text-[#1A1A1A]">{fmtEuro(total)}</span>
        </div>
        {belowMin && (
          <div className="text-xs text-[#DC2626] font-bold text-center bg-[#FEE2E2] border border-[#DC2626] rounded-lg px-3 py-2" data-testid="pr-cart-min-order-error">
            Minimum de commande : {fmtEuro(minOrder)}<br />
            <span className="font-semibold">il manque {fmtEuro(missing)}</span>
          </div>
        )}
        <button
          onClick={onOrder}
          disabled={belowMin}
          data-testid="pr-cart-column-order"
          className="w-full h-12 rounded-full bg-[#1A1A1A] hover:bg-black text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          🛵 {belowMin ? `Ajoutez ${fmtEuro(missing)}` : `Commander · ${fmtEuro(total)}`}
        </button>
      </div>
    </div>
  );
}

function InfoModal({ resto, onClose }) {
  const { t } = useTranslation();
  const fullAddr = [resto.address, resto.postal_code || "", resto.city || resto.commune || ""].filter(Boolean).join(", ");
  const mapsEmbedUrl = fullAddr ? `https://maps.google.com/maps?q=${encodeURIComponent(fullAddr)}&output=embed` : null;
  const hoursText = resto.opening_hours_text || resto.hours_summary || resto.opening_hours_summary;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="pr-info-modal">
      <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 flex items-center justify-between border-b border-[#E2DFD8]">
          <h3 className="font-head font-black text-xl">{t("publicResto.storeInfo", "Infos restaurant")}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F0EBE1] grid place-items-center" data-testid="pr-info-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 text-sm text-[#3B423E] overflow-y-auto">
          {resto.description && <p className="whitespace-pre-line leading-relaxed">{resto.description}</p>}
          {fullAddr && (
            <div>
              <b>{t("publicResto.address", "Adresse")}</b><br />
              <span className="inline-flex items-start gap-1">
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#75807A]" /> {fullAddr}
              </span>
            </div>
          )}
          {resto.phone && (
            <div><b>{t("publicResto.phone", "Téléphone")}</b><br /><a href={`tel:${resto.phone}`} className="text-[#059669] hover:underline">{resto.phone}</a></div>
          )}
          {resto.cuisine && (
            <div><b>{t("publicResto.cuisine", "Cuisine")}</b><br />{resto.cuisine}</div>
          )}
          {hoursText && (
            <div><b>{t("publicResto.hours", "Horaires")}</b><br /><span className="whitespace-pre-line">{hoursText}</span></div>
          )}
          {mapsEmbedUrl && (
            <div className="pt-2">
              <iframe
                src={mapsEmbedUrl}
                title={`Carte ${resto.name}`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="pr-info-map-embed"
                className="w-full rounded-2xl border border-[#E2DFD8]"
                style={{ height: "200px", border: 0 }}
                allowFullScreen
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CartDrawer({ cart, onClose, onAdd, onDec, onRemove, onClear, onOrder }) {
  const { t } = useTranslation();
  const total = cart.items.reduce((s, x) => s + x.qty * x.price_eur, 0);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="pr-cart-drawer">
      <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 flex items-center justify-between border-b border-[#E2DFD8]">
          <h3 className="font-head font-black text-xl inline-flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-[#059669]" /> {t("publicResto.cart.title")}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F0EBE1] grid place-items-center" data-testid="pr-cart-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {cart.items.length === 0 ? (
            <div className="text-center text-sm text-[#75807A] py-10">{t("publicResto.cart.empty")}</div>
          ) : cart.items.map((it) => (
            <div key={it.id} className="flex items-center gap-3" data-testid={`pr-cart-item-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#1A1A1A] truncate">{it.name}</div>
                <div className="text-xs text-[#75807A]">{fmtEuro(it.price_eur)} × {it.qty} = <b>{fmtEuro(it.price_eur * it.qty)}</b></div>
              </div>
              <div className="inline-flex items-center gap-1 bg-[#F0FDF4] rounded-full p-1 shrink-0">
                <button onClick={() => onDec(it.id)} className="w-7 h-7 rounded-full bg-white grid place-items-center"><Minus className="w-3.5 h-3.5" /></button>
                <span className="min-w-6 text-center font-bold text-sm text-[#059669]">{it.qty}</span>
                <button onClick={() => onAdd(it.id)} className="w-7 h-7 rounded-full bg-[#059669] text-white grid place-items-center"><Plus className="w-3.5 h-3.5" /></button>
              </div>
              <button onClick={() => onRemove(it.id)} className="w-8 h-8 rounded-full hover:bg-[#FEE2E2] grid place-items-center text-[#D32F2F] shrink-0" data-testid={`pr-cart-remove-${it.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        {cart.items.length > 0 && (
          <div className="p-5 border-t border-[#E2DFD8] space-y-3 bg-[#FDF8F0]">
            <div className="flex justify-between items-baseline">
              <span className="font-head font-bold text-lg">{t("publicResto.cart.total")}</span>
              <span className="font-head font-black text-2xl text-[#059669]">{fmtEuro(total)}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={onClear} data-testid="pr-cart-clear" className="h-12 px-4 rounded-full bg-white border border-[#E2DFD8] hover:border-[#D32F2F] hover:text-[#D32F2F] font-semibold text-sm">{t("publicResto.cart.clear")}</button>
              <button onClick={onOrder} data-testid="pr-cart-order" className="flex-1 h-12 rounded-full bg-[#1A1A1A] hover:bg-black text-white font-bold inline-flex items-center justify-center gap-2">
                🛵 {t("publicResto.cart.checkout")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Section "À découvrir" — 4 autres restaurants publiés, sélection stable par jour.
function DiscoverSection({ currentId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const r = await fetch(`${API}/restaurants?limit=24`);
        if (!r.ok) throw new Error("http");
        const d = await r.json();
        const list = Array.isArray(d) ? d : (d.items || d.restaurants || []);
        const day = Math.floor(Date.now() / (24 * 3600 * 1000));
        const filtered = list.filter((x) => x && x.id && x.id !== currentId && (x.published !== false));
        const rotated = filtered.length > 4
          ? [...filtered.slice(day % filtered.length), ...filtered.slice(0, day % filtered.length)]
          : filtered;
        if (ok) setItems(rotated.slice(0, 4));
      } catch { /* ignore */ }
      finally { if (ok) setLoading(false); }
    })();
    return () => { ok = false; };
  }, [currentId]);

  if (loading || items.length === 0) return null;

  return (
    <section className="pt-10 mt-6 border-t border-[#E2DFD8]" data-testid="pr-discover">
      <h2 className="font-head font-black text-2xl sm:text-3xl text-[#1A1A1A]">À découvrir</h2>
      <ul className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((r) => {
          const cover = mediaUrl(r.cover_url);
          return (
            <li key={r.id} data-testid={`pr-discover-${r.id}`}>
              <button
                onClick={() => { navigate(`/restaurants/${r.id}`); window.scrollTo({ top: 0 }); }}
                className="w-full text-left group"
              >
                <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-[#F0EBE1]">
                  {cover ? (
                    <img
                      src={cover}
                      alt={r.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const fb = e.currentTarget.parentNode.querySelector("[data-fallback]");
                        if (fb) fb.style.display = "grid";
                      }}
                    />
                  ) : null}
                  <div
                    data-fallback
                    className="absolute inset-0 w-full h-full place-items-center"
                    style={{ display: cover ? "none" : "grid" }}
                  >
                    <UtensilsCrossed className="w-8 h-8 text-[#75807A]" />
                  </div>
                </div>
                <div className="mt-2 font-semibold text-[#1A1A1A] text-sm line-clamp-1">{r.name}</div>
                {r.cuisine && (
                  <div className="text-xs text-[#75807A] line-clamp-1">{r.cuisine}</div>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}


// Section FAQ style Uber Eats — accordéon avec questions personnalisées au nom du resto
function FaqSection({ resto }) {
  const [openIdx, setOpenIdx] = useState(null);
  const name = resto?.name || "ce restaurant";
  const city = resto?.city || "Bruxelles";
  const faqs = [
    {
      q: `Puis-je commander une livraison de ${name} à ${city} avec Nold Go ?`,
      a: `Oui. La livraison de ${name} est disponible sur Nold Go à ${city}. Ajoutez vos plats préférés au panier et on vous les livre à vélo, en moins de 30 min en moyenne.`,
    },
    {
      q: `La livraison de ${name} est-elle disponible près de chez moi ?`,
      a: `${name} livre dans un rayon défini autour du restaurant (${resto?.address || "Bruxelles"}). Renseignez votre adresse au moment du checkout pour confirmer que vous êtes dans la zone.`,
    },
    {
      q: `Comment commander une livraison de ${name} en ligne à ${city} ?`,
      a: `Il y a 2 façons de commander sur Nold Go : via l'app PWA ou directement sur noldgo.com. Après avoir parcouru le menu de ${name}, choisissez vos plats, ajoutez-les au panier, validez votre adresse et payez en ligne. Vous suivrez ensuite le livreur en temps réel.`,
    },
    {
      q: `Où puis-je consulter les prix du menu de ${name} ?`,
      a: `Les prix des plats de ${name} sont affichés en euros directement sur cette page, à côté de chaque plat. Ils incluent la commission Nold Go.`,
    },
    {
      q: `Comment bénéficier de la livraison gratuite chez ${name} ?`,
      a: `De temps en temps, ${name} propose des promotions avec livraison gratuite à partir d'un certain montant. Consultez les bannières promo en haut de la page ou inscrivez-vous à la newsletter Nold Go pour être alerté.`,
    },
    {
      q: `Comment régler ma commande chez ${name} ?`,
      a: `Vous payez en ligne au moment de la commande par carte bancaire (Visa, Mastercard) via Stripe. Le paiement est 100% sécurisé et le débit intervient à la confirmation de la commande.`,
    },
    {
      q: `Que commander de mieux chez ${name} à ${city} ?`,
      a: `Consultez la section "Suggestions" en haut du menu — ce sont les plats les plus commandés chez ${name}. Sinon, laissez-vous tenter par les spécialités du chef en explorant les catégories du menu.`,
    },
  ];
  return (
    <section className="pt-10 mt-6 border-t border-[#E2DFD8]" data-testid="pr-faq">
      <h2 className="font-head font-black text-2xl sm:text-3xl text-[#1A1A1A]">Questions fréquentes</h2>
      <ul className="mt-5 divide-y divide-[#E2DFD8] border-t border-b border-[#E2DFD8]">
        {faqs.map((f, i) => {
          const open = openIdx === i;
          return (
            <li key={i} data-testid={`pr-faq-item-${i}`}>
              <button
                onClick={() => setOpenIdx(open ? null : i)}
                className="w-full flex items-center justify-between gap-4 py-4 text-left hover:text-[#1A1A1A]"
                aria-expanded={open}
              >
                <span className="font-semibold text-[15px] text-[#1A1A1A]">{f.q}</span>
                <ChevronDown className={`w-5 h-5 shrink-0 text-[#75807A] transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="pb-4 pr-8 text-sm text-[#3B423E] leading-relaxed" data-testid={`pr-faq-answer-${i}`}>
                  {f.a}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}


function fmtEuro(v) {
  try { return new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(v || 0); }
  catch { return `${(v || 0).toFixed(2)} €`; }
}
function slug(s) { return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }
function emptyCart() { return { restaurant_id: null, restaurant_name: null, items: [] }; }
function loadCart(id) {
  try {
    const raw = localStorage.getItem(`nold_cart_${id}`);
    if (!raw) return emptyCart();
    const parsed = JSON.parse(raw);
    return { ...emptyCart(), ...parsed };
  } catch { return emptyCart(); }
}
function saveCart(id, cart) {
  if (!id) return;
  try {
    if (!cart || (cart.items || []).length === 0) localStorage.removeItem(`nold_cart_${id}`);
    else localStorage.setItem(`nold_cart_${id}`, JSON.stringify(cart));
  } catch { /* ignore */ }
}
